import ales from "@public/static/caseStudy/ales.png";
import bombis from "@public/static/caseStudy/bombis.png";
import grafik from "@public/static/caseStudy/grafik.png";
import kaja from "@public/static/caseStudy/kaja.png";
import ruda from "@public/static/caseStudy/ruda.png";
import valy from "@public/static/caseStudy/valy.png";
import { useLocale } from "@translations/locale-context";
import { Button } from "@ui/components/button/button";
import { SectionLayout } from "@ui/components/section-layout/section-layout";
import { TextLink } from "@ui/components/text-link/text-link";
import { Trans, useTranslation } from "next-i18next";
import Image, { StaticImageData } from "next/image";
import React, { Fragment, memo } from "react";

interface ContactInfoTileProps {
    delimiter?: string;
    info: string[];
    title: string;
}

const ContactInfoTile = memo<ContactInfoTileProps>(({ delimiter = "", info, title }) => (
    <div>
        <p className="ui-typo-h5 mb-4">{title}</p>
        <p className="ui-typo-body leading-10 opacity-70">
            {info.map((el, index) => (
                <Fragment key={el}>
                    {index !== 0 && (
                        <>
                            {delimiter}
                            <br />
                        </>
                    )}
                    {el.trim()}
                </Fragment>
            ))}
        </p>
    </div>
));

ContactInfoTile.displayName = "ContactInfoTile";

interface ContactPersonTileProps {
    email: string;
    img: StaticImageData;
    name: string;
    phone?: string;
    position: string;
}

const ContactPersonTile = memo<ContactPersonTileProps>(({ email, img, name, phone, position }) => (
    <li className="ui-typo-body grid pt-8 sm:grid-cols-5 sm:grid-rows-2 xl:grid-cols-10 xl:grid-rows-1 xl:items-center xl:gap-x-8">
        <div className="mb-5 w-20 sm:col-span-1 sm:row-span-2 sm:mb-0">
            <Image alt="" src={img} width={80} height={80} />
        </div>
        <p className="ui-typo-large mb-2 sm:col-span-2 xl:col-span-3 xl:mb-0">{name}</p>
        <p className="sm:col-span-2 sm:col-start-2 xl:col-start-5">{position}</p>
        {phone && (
            <TextLink
                className="mt-6 sm:col-span-2 sm:col-start-4 sm:row-start-1 sm:mt-0 xl:col-start-7"
                href={`tel:${phone}`}
                underline
                variant="orange"
            >
                {phone}
            </TextLink>
        )}
        <TextLink
            className="mt-6 sm:col-span-2 sm:col-start-4 sm:mt-0 xl:col-start-9"
            href={`mailto:${email}`}
            underline
            variant="orange"
        >
            {email}
        </TextLink>
    </li>
));

ContactPersonTile.displayName = "ContactPersonTile";

type ContactPersonType = ContactPersonTileProps & {
    position: string;
};

const CONTACT_PERSONS: ContactPersonType[] = [
    {
        email: "fruhbauer@uxf.cz",
        img: ales,
        name: "Aleš Frühbauer",
        phone: "+420 777 753 897",
        position: "contact.ales.position",
    },
    {
        email: "tryzna@uxf.cz",
        img: bombis,
        name: "Martin Tryzna",
        phone: "+420 605 133 541",
        position: "contact.bombis.position",
    },
    {
        email: "valousek@uxf.cz",
        img: valy,
        name: "Tomáš Valoušek",
        position: "contact.valy.position",
    },
    {
        email: "krejcar@uxf.cz",
        img: ruda,
        name: "Rudolf Krejcar",
        position: "contact.ruda.position",
    },
    {
        email: "jonasova@uxf.cz",
        img: kaja,
        name: "Karolína Jonášová",
        position: "contact.kaja.position",
    },
    {
        email: "graf@uxf.cz",
        img: grafik,
        name: "Jakub Gräf",
        position: "contact.grafik.position",
    },
];

export const ContactSection = memo(() => {
    const { t } = useTranslation();
    const locale = useLocale();
    const isEn = locale === "en";

    return (
        <main className="bg-uxfViolet text-white">
            <SectionLayout
                className="xl:pb-32"
                isPage
                name={t("contact.name")}
                position="row"
                title={
                    <Trans
                        i18nKey="contact.title"
                        t={t}
                        components={{
                            highlight: <span className="text-uxfOrange" />,
                        }}
                    />
                }
            >
                <div className="md:border-t md:border-white/20 md:pt-24 lg:pt-32">
                    <div className="grid grid-rows-3 gap-y-14 sm:grid-rows-2 lg:gap-y-20 xl:grid-cols-3 xl:grid-rows-1 xl:gap-x-28">
                        <ContactInfoTile info={["IČO 06596193", "DIČ CZ06596193"]} title="UX Fans s.r.o." />
                        <ContactInfoTile delimiter="," info={t("contact.address").split(",")} title="Rezidence Royal" />
                        <div className="sm:col-span-2 xl:col-auto">
                            <Button variant="orange" href={isEn ? "/en/book-consultation" : "/sjednat-konzultaci"}>
                                {t("collaborate.button")}
                            </Button>
                        </div>
                    </div>
                </div>
            </SectionLayout>
            <ul className="ui-container mb-24 space-y-8 divide-y divide-white divide-opacity-20 md:space-y-3 lg:mb-32 xl:space-y-6">
                {CONTACT_PERSONS.map(({ position, ...rest }) => (
                    <ContactPersonTile key={rest.name} position={t(position)} {...rest} />
                ))}
            </ul>
        </main>
    );
});

ContactSection.displayName = "ContactSection";
